import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { InformModal } from '../../common/SharedComponents';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { NumericFormat } from 'react-number-format';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { formatDate, formatCurrency, handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { CommonLoanSummary } from '../common/LoanComponents';
import DateBox from 'devextreme-react/date-box';
import { CommonLoanSummaryInvokingFeatureType } from '../common/constants/CommonLoanSummaryInvokingFeatureType';

const ReleaseHoldbackModal = () => {
    const { isOpen, propertyId, releaseId = 0 } = useSelector((state) => state.loan.holdbackReleaseModal);
    const { dispatch } = store;

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
        formColumnMonths: { paddingBottom: '3px', paddingTop: '30px' },
        formRow: { marginBottom: '0' },
        highlightedFormRow: {
            marginBottom: '0',
            color: 'darkred',
            fontWeight: 'bold',
        },
        sectionHeader: { margin: '10px 0' },
        labelDiv: { width: '150px', float: 'left' },
        dataDiv: { width: '400px', float: 'left', fontWeight: 'bold' },
        bfisearch: {
            width: '250px',
            position: 'absolute',
            right: '20px',
            paddingTop: '15px',
        },
        infoSection: {
            backgroundColor: '#c0c0c07a',
            display: 'inline-block',
            width: '100%',
            padding: '5px',
            marginTop: '5px',
            marginBottom: '10px',
        },
        dataSection: {
            display: 'inline-block',
            width: '100%',
            marginBottom: '10px',
            marginTop: '10px',
        },
    };

    const [loan, set_loan] = useState(null);

    const [id, set_id] = useState(0);
    const [isFullRelease, set_isFullRelease] = useState(1);
    const [releaseDate, set_releaseDate] = useState(new Date());
    const [amount, set_amount] = useState('');
    const [notes, set_notes] = useState('');
    const [inHoldback, set_inHoldback] = useState(null);

    const [informModalOpen, set_informModalOpen] = useState(false);
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const _handleClose = () =>
        dispatch(
            loanActions.setReleaseHoldbackModal({
                holdbackReleaseModal: { isOpen: true },
            })
        );

    const isAmountValid = () => {
        let sum = 0;
        for (let x = 0; x < loan.releases.length; x++) {
            const pmt = loan.releases[x];
            if (pmt.id === id) {
                sum += amount;
            } else {
                sum += loan.releases[x].amount;
            }
        }

        return sum <= loan.holdback;
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            if (!isAmountValid()) {
                set_informModalOpen(true);
                return;
            }

            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id,
                releaseDate: new Date(releaseDate).toLocaleDateString('en-us'),
                amount: isFullRelease ? 0 : amount,
                notes,
                isFullRelease,
            };

            const response = await loanApi.setRentalHoldbackRelease(propertyId, model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan updated successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    useEffect(() => {
        async function propertyIdChanged() {
            if (propertyId && propertyId > 0) {
                await loadLoanInfo(propertyId, releaseId);
            } else {
                resetState();
            }

            set_loadingOpen(false);
        }

        propertyIdChanged();
    }, [isOpen, propertyId, releaseId]);

    useEffect(() => {
        if (!loan) {
            return;
        }
    }, [loan]);

    const resetState = () => {
        set_id(0);
        set_isFullRelease(1);
        set_loan(null);
        set_releaseDate(new Date());
        set_amount('');
        set_notes(null);
        set_inHoldback(null);
    };

    const releasedToDateForIndex = (releases, lastIndex) => {
        let sum = 0;
        for (let x = 0; x < lastIndex; x++) {
            sum += releases[x].amount;
        }
        return sum;
    };

    const loadLoanInfo = async (propertyId, releaseId) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await loanApi.getRentalHoldbackReleases(propertyId);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }
        const loan = response.result;
        set_loan(loan);

        if (releaseId && releaseId > 0) {
            const idx = loan.releases.findIndex((a) => a.id === releaseId);
            if (idx > -1) {
                const hb = loan.releases[idx];
                const rlsdToDate = releasedToDateForIndex(loan.releases, idx);
                set_id(releaseId);
                set_isFullRelease(hb.isFullRelease ? 1 : 0);
                set_releaseDate(hb.releaseDate ? new Date(hb.releaseDate) : null);
                set_amount(hb.amount);
                set_notes(hb.notes);
                set_inHoldback(loan.holdback - rlsdToDate);
            }
        } else {
            set_inHoldback(loan.holdback - loan.releasedToDate);
        }

        set_loadingOpen(false);
    };

    if (!isOpen || !loan) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '600px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>Release Holdback</h1>
                                    <div style={styles.bfisearch}>
                                        {/* <BfiNumberSearch loanTypeId={loan?.loanTypeId} onSelect={selectBfiNumber} /> */}
                                    </div>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                            </div>

                            <div
                                className='standard-form no-search'
                                style={{ padding: '0', margin: '0' }}
                            >
                                <form
                                    name='releaseHoldbackForm'
                                    onSubmit={(event) => onSubmit(event)}
                                >
                                    <div style={{ padding: '0px 7px' }}>
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            {loan.releases && loan.releases.length > 0 && (
                                                <div style={styles.infoSection}>
                                                    <div>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-12'
                                                                style={styles.formColumn}
                                                            >
                                                                Initial Holdback: {formatCurrency(loan.holdback, 0)}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-2'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>Rls #</b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>Date</b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>Amount</b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-4'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>Remaining Holdback</b>
                                                            </div>
                                                        </div>
                                                        {loan.releases.map((r, index) => {
                                                            return (
                                                                <div
                                                                    className='row'
                                                                    style={
                                                                        id === r.id ? styles.highlightedFormRow : styles.formRow
                                                                    }
                                                                >
                                                                    <div
                                                                        className='columns small-12 large-2'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {index + 1}
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-3'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {formatDate(r.releaseDate)}
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-3'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {formatCurrency(r.amount, 0)}
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-4'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {formatCurrency(r.remainingHoldback, 0)}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            <div style={styles.dataSection}>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={styles.formColumn}
                                                    >
                                                        <input
                                                            type='radio'
                                                            name='paymenttype'
                                                            value={isFullRelease}
                                                            checked={isFullRelease === 1}
                                                            onChange={(e) => set_isFullRelease(1)}
                                                            disabled={releaseId}
                                                        />
                                                        &nbsp;Whole &nbsp;&nbsp;&nbsp;
                                                        <input
                                                            type='radio'
                                                            name='paymenttype'
                                                            value={!isFullRelease}
                                                            checked={isFullRelease === 0}
                                                            onChange={(e) => set_isFullRelease(0)}
                                                            disabled={releaseId}
                                                        />
                                                        &nbsp;Partial
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-4'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='releaseDate'>
                                                        Date
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <DateBox
                                                        id='releaseDate'
                                                        value={releaseDate}
                                                        onValueChange={set_releaseDate}
                                                        required={true}
                                                    />
                                                </div>
                                                {isFullRelease === 1 && (
                                                    <div
                                                        className='columns small-12 large-4'
                                                        style={styles.formColumn}
                                                    >
                                                        <label htmlFor='principal'>In Holdback: </label>
                                                        <NumericFormat
                                                            style={{
                                                                border: 'none',
                                                                backgroundColor: 'initial',
                                                            }}
                                                            thousandSeparator=','
                                                            prefix='$'
                                                            id='principal'
                                                            name='principal'
                                                            placeholder='$'
                                                            value={inHoldback}
                                                            decimalScale={2}
                                                            allowNegative={false}
                                                            displayType='input'
                                                            disabled
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {isFullRelease === 0 && (
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-7'
                                                        style={styles.formColumn}
                                                    >
                                                        <table>
                                                            <tr>
                                                                <td
                                                                    style={{
                                                                        width: '160px',
                                                                    }}
                                                                >
                                                                    <label htmlFor='holdback'>In Holdback: </label>
                                                                </td>
                                                                <td>
                                                                    <NumericFormat
                                                                        style={{
                                                                            border: 'none',
                                                                            backgroundColor: 'initial',
                                                                        }}
                                                                        thousandSeparator=','
                                                                        prefix='$'
                                                                        id='holdback'
                                                                        name='holdback'
                                                                        placeholder='$'
                                                                        value={inHoldback}
                                                                        decimalScale={2}
                                                                        allowNegative={false}
                                                                        displayType='input'
                                                                        disabled
                                                                    />
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td>
                                                                    <label htmlFor='amount'>
                                                                        Amount to Release: <span className='req'>*</span>
                                                                    </label>
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        paddingBottom: '3px',
                                                                    }}
                                                                >
                                                                    <NumericFormat
                                                                        thousandSeparator=','
                                                                        prefix='$'
                                                                        id='amount'
                                                                        name='amount'
                                                                        placeholder='$'
                                                                        value={amount}
                                                                        decimalScale={2}
                                                                        allowNegative={false}
                                                                        onValueChange={(values) => {
                                                                            set_amount(values.floatValue);
                                                                        }}
                                                                        required={true}
                                                                        isAllowed={(values, sourceInfo) => {
                                                                            const value = values?.floatValue;
                                                                            return (
                                                                                value === undefined ||
                                                                                value === null ||
                                                                                (value > 0 && value <= inHoldback)
                                                                            );
                                                                        }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                            <tr
                                                                style={{
                                                                    borderTop: '1px solid silver',
                                                                }}
                                                            >
                                                                <td>
                                                                    <label htmlFor='payDate'>Remaining: </label>
                                                                </td>
                                                                <td>
                                                                    <NumericFormat
                                                                        style={{
                                                                            border: 'none',
                                                                            backgroundColor: 'initial',
                                                                        }}
                                                                        thousandSeparator=','
                                                                        prefix='$'
                                                                        id='newPrincipal'
                                                                        name='newPrincipal'
                                                                        value={amount && amount > 0 ? inHoldback - amount : null}
                                                                        decimalScale={2}
                                                                        displayType='input'
                                                                        disabled
                                                                    />
                                                                </td>
                                                            </tr>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='notes'>Notes</label>
                                                    <textarea
                                                        value={notes}
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                        rows={4}
                                                        id='notes'
                                                        onChange={(e) => set_notes(e.target.value)}
                                                        required={false}
                                                        placeholder=''
                                                        maxLength={2000}
                                                    />
                                                </div>
                                            </div>
                                            <CommonLoanSummary
                                                loan={loan}
                                                invokingFeature={CommonLoanSummaryInvokingFeatureType.ReleaseHoldback}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <InformModal
                isOpen={informModalOpen}
                ititle={'Error'}
                text={'The sum of all the releases would exceed the total Holdback!'}
                confirmCallback={() => set_informModalOpen(false)}
            />
        </>
    );
};

export default ReleaseHoldbackModal;
